// @flow

import type {
  Category,
  CookieLifetime,
  CookieSameSiteSetting,
  SetCookieOptions,
} from '../types';

const isBrowser = typeof window !== 'undefined';

/**
 * Represents a known cookie read from the {CookieManifest}.
 * @param {String} name The name of the cookie.
 * @param {$Shape<Cookie>} attrs Attributes read from the entry in the cookie manifest.
 * @private
 */
export class Cookie {
  _regexp: ?RegExp;

  /**
   * The name of the cookie. For inexact cookies, this can be a pattern using asterisks as placeholders
   * for any characters.
   */
  name: string;

  /**
   * If true, allow name to represent a pattern instead of a direct match.
   */
  inexact: boolean;

  /**
   * The cookie's category (preferences, advertising, etc.)
   */
  category: Category;

  /**
   * The maximum number of seconds after which the cookie expires, or "session" if the cookie is not persistent.
   */
  lifetime: CookieLifetime;

  /**
   * A list of domains on which the cookie is allowed to be set. This can also include the value "request",
   * which indicates that the cookie can be set on the domain of the current request.
   */
  domains: string[];

  /**
   * The HttpOnly setting for the cookie. Since HttpOnly cookies can't be set via JavaScript, this is just
   * used for showing errors.
   */
  httpOnly: boolean;

  /**
   * The SameSite setting for the cookie.
   */
  sameSite: CookieSameSiteSetting;

  /**
   * Indicates that the cookie can only be read via HTTPS.
   */
  secure: boolean;

  constructor(name: string, attrs: $Shape<Cookie>) {
    this.name = name;
    this.category = attrs.category;
    this.lifetime = attrs.lifetime;
    this.domains = attrs.domains;
    this.secure = attrs.secure === false ? false : true;
    this.httpOnly = attrs.httpOnly === false ? false : true;
    this.sameSite = attrs.sameSite || 'Lax';

    if (this.name.indexOf('*') >= 0) {
      this.inexact = true;
      this._regexp = new RegExp(name.replace('*', '.+'));
    }
  }

  /**
   * Determines whether the given cookie name matches this cookie's name. Performs an inexact
   * match if this cookie supports inexact (pattern-based) matching.
   * @param {String} str The string to match.
   */
  isMatch(str: string) {
    return this.name === str || (this._regexp && this._regexp.test(str));
  }

  /**
   * Given the settings from the manifest and the domain passed in at the callsite, determines
   * on which domain the cookie should be set.
   * @param {string} desiredDomain The domain requested at the callsite.
   * @param {Map} overrides A map of domain overrides to apply.
   */
  resolveDomain(desiredDomain: ?string, overrides: {[domain: string]: string}) {
    // Apply any domain overrides. This is used to rewrite the domains for cookies during testing.
    const domains = this.domains.map((domain) => overrides[domain] || domain);

    // Allow any cookie to be set on devbox domains.
    // TODO (nkohari): Could this be implemented using overrides instead?
    if (desiredDomain && desiredDomain.match('dev.stripe.me')) {
      return '.dev.stripe.me';
    }

    // The value of the domain option must exist in the cookie's domains array.
    if (desiredDomain && domains.indexOf(desiredDomain) < 0) {
      throw new Error(
        `The ${this.name} cookie is not allowed to be set on the ${desiredDomain} domain.`,
      );
    }

    const domain = desiredDomain || domains[0];

    // If the domain resolves to "request', use the current domain as long as we're running
    // in a browser environment.
    if (domain === 'request') {
      if (!isBrowser) {
        throw new Error(
          `Cannot set ${this.name} cookie to the current request, because we aren't running in a browser.`,
        );
      }
      return window.location.hostname;
    }

    return domain;
  }

  /**
   * Given the settings from the manifest and the lifetime passed in at the callsite, determines
   * the expiry that should be set on the cookie.
   * @param {number} desiredLifetime The lifetime (in seconds) requested at the callsite.
   */
  resolveExpiry(desiredLifetime: ?number): Date | null {
    // TODO (nkohari): What should we be doing for local storage?
    if (this.lifetime === 'forever') {
      return null;
    }

    if (this.lifetime === 'session') {
      // Session cookies cannot have a lifetime set.
      if (desiredLifetime) {
        throw new Error(
          `The ${this.name} cookie cannot have a lifetime, because it is a session cookie.`,
        );
      }

      // If the cookie is a session cookie, it has no expiration, and it will
      // expire when the user closes their browser.
      return null;
    }

    let lifetime = this.lifetime;

    if (desiredLifetime) {
      // The lifetime option cannot be greater than the cookie's lifetime in the manifest.
      if (this.lifetime < desiredLifetime) {
        throw new Error(
          `The ${this.name} cookie has a maximum lifetime of ${this.lifetime} seconds.`,
        );
      }

      lifetime = desiredLifetime;
    }

    // The js-cookie library expects either a Date or a number of days from now. Since
    // some of our cookies have lifetimes less than a single day, we use Dates.
    return new Date(Date.now() + lifetime * 1000);
  }
}
